/**
 * ProInspector.tsx
 * 
 * Context-aware inspector panel for the Pro editor.
 * Replaces the 8-tab RightMenu with a smart panel that shows
 * the right content based on what's selected or active.
 */

import React, { useMemo, useState, useEffect } from 'react';
import { MessageSquare, Captions, Download, Mic, Library, UserCircle, Sliders, X, Volume1, Volume2, VolumeX, Trash } from 'lucide-react';
import type { Clip, Track, Subtitle, SubtitleStyle, ChatMessage, EditingSuggestion, ChatPendingChoice } from '../../types';
import { AspectRatio } from '../../media-recorder-exporter';
import { GeneratedVoice } from '../../components/RightMenu/constants';
import firebase from 'firebase/compat/app';

// Import existing tab components from the main app
import ChatTab from '../../components/RightMenu/ChatTab';
import SubtitlesTab from '../../components/RightMenu/SubtitlesTab';
import ExportTab from '../../components/RightMenu/ExportTab';
import VoiceoverTab from '../../components/RightMenu/VoiceoverTab';
import LibraryTab from '../../components/RightMenu/LibraryTab';
import AccountTab from '../../components/RightMenu/AccountTab';
import { MasterTab } from '../../components/RightMenu/MasterTab';
import TransformControls from '../../components/RightMenu/TransformControls';
import { updateClipDeep } from '../../utils/clip-helpers';

interface ProInspectorProps {
    isOpen: boolean;
    activeTab: string;
    setActiveTab: (tab: string) => void;
    selectedClip: Clip | null;
    allClips: Clip[];
    setAllClips: React.Dispatch<React.SetStateAction<Clip[]>>;
    tracks: Track[];
    messages?: ChatMessage[];
    onSendMessage?: (message: string, elements?: any) => void;
    onChoiceSelect?: (choice: 'current' | 'original' | 'yes' | 'no', pendingChoice: ChatPendingChoice) => void;
    editingSuggestions?: EditingSuggestion[];

    // Export
    isExporting?: boolean;
    exportStatus?: string;
    exportProgress?: number;
    exportDetails?: any[];
    exportedVideos?: any[];
    onStartExport?: (options: any) => void;
    onCancelExport?: () => void;
    exportAspectRatio: AspectRatio;
    onExportAspectRatioChange: (ratio: AspectRatio) => void;
    isTimelineEligibleForFastExport: boolean;
    vfxSegmentPreviews?: any[];
    onStartServerExport?: () => void;
    serverExportStatus?: any;
    onCancelServerExport?: () => void;
    onDeleteExport?: (videoId: string, videoUrl: string) => void;
    projectName?: string;
    chunks?: any[];
    isMerging?: boolean;
    onRetryChunk?: (chunkIndex: number) => void;
    onStartHybridExport?: () => void;
    hasTimelineOverlays?: boolean;
    totalTimelineDuration?: number;
    activeServerExports?: any[];

    // Subtitles
    subtitleStyle: SubtitleStyle;
    setSubtitleStyle: React.Dispatch<React.SetStateAction<SubtitleStyle>>;
    onSeekRequest: (time: number) => void;
    activeSubtitleLanguage: string;
    onActiveSubtitleLanguageChange: (lang: string) => void;
    onTranslateSubtitles?: (targetLang: string) => void;
    translationsInProgress?: Set<string>;
    selectedClipId: string | null;
    setSelectedClipId: (id: string | null) => void;
    selectedSubtitleId?: number | string | null;

    // Actions
    onDeleteClip?: (clipId: string) => void;

    // Volume
    onClipVolumeChange?: (volume: number) => void;
    onClipVolumeCommit?: (volume: number) => void;

    // Library
    onAddLibraryItem?: (item: any, category: any, mode?: string) => void;
    generatedVoices?: GeneratedVoice[];
    setGeneratedVoices?: React.Dispatch<React.SetStateAction<GeneratedVoice[]>>;
    voiceoverText?: string;
    setVoiceoverText?: React.Dispatch<React.SetStateAction<string>>;

    // Master
    currentScope?: any;
    isRestrictedMode?: boolean;
    onSaveMaster?: (data: any) => void;

    user: firebase.User | null;
    scaleFactor?: number;
    setScaleFactor?: (factor: number) => void;
}

export const ProInspector: React.FC<ProInspectorProps> = ({
    isOpen,
    activeTab,
    setActiveTab,
    selectedClip,
    allClips,
    setAllClips,
    tracks,
    messages = [],
    onSendMessage = (_message: string, _elements?: any) => { },
    onChoiceSelect = () => { },
    editingSuggestions = [],

    isExporting,
    exportStatus,
    exportProgress,
    exportDetails,
    exportedVideos,
    onStartExport,
    onCancelExport,
    exportAspectRatio,
    onExportAspectRatioChange,
    isTimelineEligibleForFastExport,
    vfxSegmentPreviews,
    onStartServerExport,
    serverExportStatus,
    onCancelServerExport,
    onDeleteExport,
    projectName,
    chunks,
    isMerging,
    onRetryChunk,
    onStartHybridExport,
    hasTimelineOverlays,
    totalTimelineDuration,
    activeServerExports,

    subtitleStyle,
    setSubtitleStyle,
    onSeekRequest,
    activeSubtitleLanguage,
    onActiveSubtitleLanguageChange,
    onTranslateSubtitles,
    translationsInProgress,
    selectedClipId,
    setSelectedClipId,
    selectedSubtitleId,

    onDeleteClip,

    onClipVolumeChange,
    onClipVolumeCommit,

    onAddLibraryItem,
    generatedVoices,
    setGeneratedVoices,
    voiceoverText,
    setVoiceoverText,

    currentScope,
    isRestrictedMode,
    onSaveMaster,

    user,
    scaleFactor = 1,
    setScaleFactor = () => { },
}) => {
    // Determine which tabs to show
    const tabs = useMemo(() => {
        const allTabs = [
            { id: 'assistant', icon: MessageSquare, label: 'Chat' },
            { id: 'subtitles', icon: Captions, label: 'Sous-titres' },
            { id: 'library', icon: Library, label: 'Bibliothèque' },
            { id: 'voiceover', icon: Mic, label: 'Voix' },
            { id: 'export', icon: Download, label: 'Export' },
            { id: 'account', icon: UserCircle, label: 'Compte' },
        ];

        if (isRestrictedMode) {
            return [
                { id: 'master', icon: Sliders, label: 'Master' },
                ...allTabs,
            ];
        }

        return allTabs;
    }, [isRestrictedMode]);

    // Transform change handler for clips
    const handleTransformChange = (clipId: string, updates: Partial<Clip>) => {
        setAllClips(prev => updateClipDeep(prev, clipId, c => ({ ...c, ...updates })));
    };

    // Show transform controls when a clip has a transform
    const canShowTransformControls = useMemo(() => {
        if (!selectedClip) return false;
        const isZoomClip = selectedClip.type === 'zoom';
        const hasTransform = isZoomClip ? !!selectedClip.endTransform : !!selectedClip.transform;
        return hasTransform;
    }, [selectedClip]);

    // Show volume panel for audio clips (and pure audio video clips)
    const canShowVolumePanel = useMemo(() => {
        if (!selectedClip) return false;
        if (selectedClip.type === 'audio') return true;
        if (selectedClip.type !== 'video') return false;
        const url = selectedClip.url || '';
        return !url.toLowerCase().endsWith('.gif');
    }, [selectedClip]);

    const [localVolume, setLocalVolume] = useState(selectedClip?.volume ?? 1);
    useEffect(() => {
        setLocalVolume(selectedClip?.volume ?? 1);
    }, [selectedClip?.id, selectedClip?.volume]);

    const VolumeIcon = localVolume > 1 ? Volume2 : localVolume > 0 ? Volume1 : VolumeX;

    if (!isOpen) {
        return <div className="pro-inspector collapsed" />;
    }

    return (
        <div className="pro-inspector">
            {/* Tabs */}
            <div className="pro-inspector__tabs">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`pro-inspector__tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                        title={tab.label}
                    >
                        <tab.icon size={13} />
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="pro-inspector__content">
                {/* Clip Transform Controls (overlay when clip selected) */}
                {canShowTransformControls && selectedClip && activeTab === 'assistant' && (
                    <div style={{
                        borderBottom: '1px solid var(--pro-border)',
                        background: 'var(--pro-bg-panel)'
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '8px 12px', borderBottom: '1px solid var(--pro-border-subtle)'
                        }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--pro-text-secondary)' }}>
                                {selectedClip.name}
                            </span>
                            <button
                                className="pro-btn pro-btn--icon"
                                style={{ width: 24, height: 24 }}
                                onClick={() => setSelectedClipId(null)}
                            >
                                <X size={12} />
                            </button>
                        </div>
                        <TransformControls
                            clip={selectedClip}
                            onClose={() => setSelectedClipId(null)}
                            onTransformChange={handleTransformChange}
                            onVolumeChange={onClipVolumeChange}
                            onVolumeCommit={onClipVolumeCommit}
                        />
                    </div>
                )}

                {/* Audio Volume Panel - shown above chat for audio/music clips */}
                {canShowVolumePanel && !canShowTransformControls && selectedClip && activeTab === 'assistant' && (
                    <div style={{
                        borderBottom: '1px solid var(--pro-border)',
                        background: 'var(--pro-bg-panel)',
                        padding: '10px 12px',
                        flexShrink: 0,
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: '8px',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <VolumeIcon size={14} style={{ color: 'var(--pro-text-secondary)' }} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--pro-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
                                    {selectedClip.name}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: 11, color: 'var(--pro-text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                                    {Math.round(localVolume * 100)}%
                                </span>
                                <button
                                    className="pro-btn pro-btn--icon"
                                    style={{ width: 22, height: 22 }}
                                    onClick={() => setSelectedClipId(null)}
                                >
                                    <X size={11} />
                                </button>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="range"
                                min="0"
                                max="2"
                                step="0.01"
                                value={localVolume}
                                onChange={(e) => {
                                    const v = Number((e.target as HTMLInputElement).value);
                                    setLocalVolume(v);
                                    onClipVolumeChange?.(v);
                                }}
                                onMouseUp={() => onClipVolumeCommit?.(localVolume)}
                                onTouchEnd={() => onClipVolumeCommit?.(localVolume)}
                                style={{ flex: 1, cursor: 'pointer', accentColor: 'var(--pro-accent)' }}
                            />
                            <button
                                className="pro-btn"
                                style={{
                                    padding: '4px', height: '28px', width: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)', border: 'none'
                                }}
                                onClick={() => {
                                    if (selectedClipId && onDeleteClip) {
                                        onDeleteClip(selectedClipId);
                                        setSelectedClipId(null);
                                    }
                                }}
                                title="Supprimer la piste"
                            >
                                <Trash size={14} />
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'master' && isRestrictedMode && currentScope && (
                    <MasterTab
                        currentScope={currentScope}
                        onUpdate={(updates) => handleTransformChange(currentScope.id, updates)}
                        onSaveMaster={onSaveMaster}
                    />
                )}

                {activeTab === 'assistant' && (
                    <ChatTab
                        messages={messages}
                        onSendMessage={(msg, elements) => onSendMessage(msg, elements)}
                        onChoiceSelect={onChoiceSelect}
                        editingSuggestions={editingSuggestions}
                    />
                )}

                {activeTab === 'subtitles' && (
                    <SubtitlesTab
                        subtitleStyle={subtitleStyle}
                        setSubtitleStyle={setSubtitleStyle}
                        onSeekRequest={onSeekRequest}
                        activeSubtitleLanguage={activeSubtitleLanguage}
                        onActiveSubtitleLanguageChange={onActiveSubtitleLanguageChange}
                        onTranslateSubtitles={onTranslateSubtitles}
                        translationsInProgress={translationsInProgress}
                        allClips={allClips}
                        setAllClips={setAllClips}
                        selectedClipId={selectedClipId}
                        setSelectedClipId={setSelectedClipId}
                        selectedSubtitleId={selectedSubtitleId}
                    />
                )}

                {activeTab === 'library' && (
                    <LibraryTab
                        onAddLibraryItem={onAddLibraryItem!}
                        allClips={allClips}
                        setAllClips={setAllClips}
                        tracks={tracks}
                        user={user}
                        selectedClip={selectedClip}
                    />
                )}

                {activeTab === 'voiceover' && (
                    <VoiceoverTab
                        onAddLibraryItem={onAddLibraryItem!}
                        generatedVoices={generatedVoices}
                        setGeneratedVoices={setGeneratedVoices}
                        voiceoverText={voiceoverText}
                        setVoiceoverText={setVoiceoverText}
                    />
                )}

                {activeTab === 'export' && (
                    <ExportTab
                        isExporting={isExporting}
                        exportStatus={exportStatus}
                        exportProgress={exportProgress}
                        exportDetails={exportDetails}
                        exportedVideos={exportedVideos}
                        onStartExport={onStartExport}
                        onCancelExport={onCancelExport}
                        exportAspectRatio={exportAspectRatio}
                        onExportAspectRatioChange={onExportAspectRatioChange}
                        isTimelineEligibleForFastExport={isTimelineEligibleForFastExport}
                        vfxSegmentPreviews={vfxSegmentPreviews}
                        onStartServerExport={onStartServerExport}
                        serverExportStatus={serverExportStatus}
                        onCancelServerExport={onCancelServerExport}
                        onDeleteExport={onDeleteExport}
                        projectName={projectName}
                        chunks={chunks || []}
                        isMerging={isMerging || false}
                        onRetryChunk={onRetryChunk}
                        onStartHybridExport={onStartHybridExport}
                        hasTimelineOverlays={hasTimelineOverlays || false}
                        totalTimelineDuration={totalTimelineDuration || 0}
                        activeServerExports={activeServerExports || []}
                    />
                )}

                {activeTab === 'account' && (
                    <AccountTab
                        user={user}
                        scaleFactor={scaleFactor}
                        setScaleFactor={setScaleFactor}
                    />
                )}
            </div>
        </div>
    );
};
